import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function VaRCalculator() {
  const [portfolioValue, setPortfolioValue] = useState("");
  const [expectedReturn, setExpectedReturn] = useState("");
  const [stdDev, setStdDev] = useState("");
  const [confidenceLevel, setConfidenceLevel] = useState("95");
  const [timeHorizon, setTimeHorizon] = useState("");
  const [result, setResult] = useState<{ var: number; percentage: number } | null>(null);

  const calculate = () => {
    const value = parseFloat(portfolioValue);
    const returnRate = parseFloat(expectedReturn) / 100;
    const volatility = parseFloat(stdDev) / 100;
    const days = parseFloat(timeHorizon);

    if (isNaN(value) || isNaN(returnRate) || isNaN(volatility) || isNaN(days) || 
        value <= 0 || volatility <= 0 || days <= 0) {
      toast.error("Please enter valid values");
      return;
    }

    // Z-scores for confidence levels
    const zScores: Record<string, number> = {
      "90": 1.282,
      "95": 1.645,
      "99": 2.326
    };

    const z = zScores[confidenceLevel];
    
    // VaR = Portfolio Value × (Z-score × σ × √t - μ × t)
    const sqrtTime = Math.sqrt(days / 252); // Assuming 252 trading days
    const varAmount = value * (z * volatility * sqrtTime - returnRate * (days / 252));
    const varPercentage = (varAmount / value) * 100;

    setResult({ var: varAmount, percentage: varPercentage });
    toast.success("VaR calculated!");
  };

  const reset = () => {
    setPortfolioValue("");
    setExpectedReturn("");
    setStdDev("");
    setConfidenceLevel("95");
    setTimeHorizon("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Value at Risk (VaR) Calculator</CardTitle>
          <CardDescription>
            Estimate potential portfolio loss over a given time period
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="value">Portfolio Value ($)</Label>
            <Input
              id="value"
              type="number"
              placeholder="1000000"
              value={portfolioValue}
              onChange={(e) => setPortfolioValue(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="return">Expected Annual Return (%)</Label>
            <Input
              id="return"
              type="number"
              placeholder="10"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="stdDev">Annual Volatility (Std Dev) (%)</Label>
            <Input
              id="stdDev"
              type="number"
              placeholder="20"
              value={stdDev}
              onChange={(e) => setStdDev(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confidence">Confidence Level</Label>
            <Select value={confidenceLevel} onValueChange={setConfidenceLevel}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="90">90%</SelectItem>
                <SelectItem value="95">95%</SelectItem>
                <SelectItem value="99">99%</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="time">Time Horizon (days)</Label>
            <Input
              id="time"
              type="number"
              placeholder="10"
              value={timeHorizon}
              onChange={(e) => setTimeHorizon(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 space-y-3">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-lg">
                    <strong>VaR ({confidenceLevel}% confidence):</strong> ${result.var.toFixed(2)}
                  </p>
                  <CopyResultButton value={result.var.toFixed(2)} />
                </div>
                <p className="text-sm mt-1">
                  ({result.percentage.toFixed(2)}% of portfolio)
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                This represents the maximum loss expected with {confidenceLevel}% confidence over the specified time period.
              </p>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["var"]} trigger={result?.var} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
